import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';

import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import BarLoaderOverlayComponent from 'webapp/components/bar-loader-overlay';
// this one works without resolution center messages, and edits the block ms / ds directly (block.replaceGenericMetrics)
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import ResolveDataItemChoiceComponent from 'webapp/components/resolve-data-item-choice';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { SourceData } from 'webapp/models-custom/resolve-sources-data';
import type SourceType from 'webapp/models-custom/source-type';

import type { onConnectSourceResult } from 'webapp/services/connect';

export default class BlockPopupResolveDataComponent extends PopupInnerModalComponent<PopupData.Block.Resolve> {
	@service('connect') declare connectService: Service.Connect;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked sourceTypesToResolve: SourceType[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.block.missingSourceTypeIds.map((sourceTypeId) => {
			const sourceType = this.mmSourceTypesService.findModelById(sourceTypeId);
			if (sourceType) {
				this.sourceTypesToResolve.push(sourceType);
			}
		});
	}

	get block() {
		return this.popupData.model;
	}

	get sourcesToConnect() {
		const sources: SourceData[] = [];
		this.sourceTypesToResolve.forEach((sourceType) => {
			if (!sourceType.connectedSources.length) {
				sources.push({
					sourceType,
				});
			}
		});
		return sources;
	}

	get sourcesToChoose() {
		const sources: SourceData[] = [];
		this.sourceTypesToResolve.forEach((sourceType) => {
			sources.push({
				sourceType,
				isSolving: false,
			});
		});
		return sources;
	}

	get numberOfSourcesToSolve() {
		return this.block.missingSourceTypeIds.length;
	}

	get isContinueButtonDisabled() {
		return this.numberOfSourcesToSolve !== 0;
	}

	resolve(sourceTypeId: number, sourceId: string) {
		void this.block.replaceGenericMetrics(sourceTypeId, sourceId);
	}

	@action
	connect(sourceToResolve: SourceData) {
		void this.connectService
			.connectSourceFromIdOrKey(sourceToResolve.sourceType.key, {
				origin: 'modalResolvedata',
			})
			.then((result: onConnectSourceResult) => {
				if (result.isSuccess) {
					// after the source metrics are added to store
					next(() => {
						this.resolve(sourceToResolve.sourceType.id!, String(result.source.id));
					});
				}
			});
	}

	@action
	resolveSource(sourceToResolve: SourceData, sourceId: string) {
		sourceToResolve.isSolving = true;
		this.resolve(sourceToResolve.sourceType.id!, sourceId);
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Connect" @size="lg" />
			Connect data sources used on '{{this.block.name}}'
		</div>
		<div class="content">

			<p>Datablock '{{this.block.name}}' is using the following data sources:</p>

			<div class="actionList">
				<ul class="sources-map">
					{{#each this.sourcesToConnect as |source|}}
						<li class="t1">
							<AvatarOrInitialsComponent
								@name={{source.sourceType.name}}
								@avatar={{source.sourceType.avatar}}
							/>
							<span class="sourceTitle">{{source.sourceType.name}}</span>
							<a
								role="button"
								class="button theme-green"
								{{on "click" (fn this.connect source)}}
							>
								<CommonUiIconComponent @name="Connect" @size="sm" />Connect
							</a>
						</li>
					{{/each}}
					{{#each this.sourcesToChoose as |source|}}
						<li class="t2">
							<AvatarOrInitialsComponent
								@name={{source.sourceType.name}}
								@avatar={{source.sourceType.avatar}}
							/>
							<span class="sourceTitle">{{source.sourceType.name}}</span>
							<ResolveDataItemChoiceComponent
								@sourceData={{source}}
								@onChoose={{this.resolveSource}}
							/>
							<BarLoaderOverlayComponent @isActive={{source.isSolving}} />
						</li>
					{{/each}}
				</ul>
			</div>
			<div class="controls2">
				<div class="controls2__left">
					{{#if this.isContinueButtonDisabled}}
						<a role="button" class="later" {{on "click" this.close}}>
							No thanks, I will connect later
						</a>
					{{/if}}
				</div>
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Continue"
						@isDisabled={{this.isContinueButtonDisabled}}
						@onTrigger={{this.close}}
						@extraClass="continue"
					/>
				</div>
			</div>
		</div>
	</template>
}
